import styles from './index.less'

const stateKey = 'dragSelectPipelineState'

const thresholdPixel = 4

const dragSelectPipeline = pipeline => {
    const state = pipeline.getStateAtKey(stateKey) ?? {}
    const {
        mouseDown,
        isDragging,
        hasSelection,
        selectStartRowIndex,
        selectStartColumnIndex,
        selectEndRowIndex,
        selectEndColumnIndex,
    } = state

    const gGetCellProps = (column, columnIndex) => {
        return (value, row, rowIndex) => {
            const onMouseDown = () => {
                pipeline.setStateAtKey(stateKey, {
                    ...state,
                    hasSelection: false,
                    mouseDown: true,
                })
            }
            const onMouseUp = () => {
                pipeline.setStateAtKey(stateKey, {
                    ...state,
                    mouseDown: false,
                    isDragging: false,
                })
            }
            const dragStart = () => {
                document.getSelection().removeAllRanges()
                pipeline.setStateAtKey(stateKey, {
                    ...state,
                    isDragging: true,
                    hasSelection: true,
                    selectStartRowIndex: rowIndex,
                    selectStartColumnIndex: columnIndex,
                    selectEndRowIndex: rowIndex,
                    selectEndColumnIndex: columnIndex,
                })
            }

            const dragMove = () => {
                if (rowIndex !== selectEndRowIndex || columnIndex !== selectEndColumnIndex) {
                    pipeline.setStateAtKey(stateKey, {
                        ...state,
                        selectEndRowIndex: rowIndex,
                        selectEndColumnIndex: columnIndex,
                    })
                }
            }
            const onMouseMove = e => {
                if (!mouseDown) {
                    return
                }
                if (isDragging) {
                    dragMove()
                } else {
                    const { movementX, movementY } = e
                    if (Math.abs(movementX) + Math.abs(movementY) > thresholdPixel) {
                        dragStart()
                    }
                }
            }
            const cellProps = {
                onMouseDown,
                onMouseUp,
                onMouseMove,
            }
            cellProps.style = { userSelect: 'none' }
            if (hasSelection) {
                const classNames = []
                const minSelectRowIndex = Math.min(selectStartRowIndex, selectEndRowIndex)
                const maxSelectRowIndex = Math.max(selectStartRowIndex, selectEndRowIndex)
                const minSelectColumnIndex = Math.min(selectStartColumnIndex, selectEndColumnIndex)
                const maxSelectColumnIndex = Math.max(selectStartColumnIndex, selectEndColumnIndex)
                if (
                    rowIndex >= minSelectRowIndex &&
                    rowIndex <= maxSelectRowIndex &&
                    columnIndex >= minSelectColumnIndex &&
                    columnIndex <= maxSelectColumnIndex
                ) {
                    classNames.push(styles.selected)
                    if (rowIndex === minSelectRowIndex) {
                        classNames.push(styles.selectedFirstRow)
                    }
                    if (rowIndex === maxSelectRowIndex) {
                        classNames.push(styles.selectedLastRow)
                    }
                    if (columnIndex === minSelectColumnIndex) {
                        classNames.push(styles.selectedFirstColumn)
                    }
                    if (columnIndex === maxSelectColumnIndex) {
                        classNames.push(styles.selectedLastColumn)
                    }
                }
                cellProps.className = classNames.join(' ')
            }
            return cellProps
        }
    }

    const processColumns = columns => {
        return columns.map((column, columnIndex) => ({
            ...column,
            getCellProps: gGetCellProps(column, columnIndex),
        }))
    }

    pipeline.mapColumns(processColumns)

    pipeline.getSelection = ({ columnOffset = 0 } = {}) => {
        if (!hasSelection) {
            return
        }
        const rangeCount = document.getSelection().rangeCount
        if (rangeCount) {
            return
        }
        const dataSource = pipeline.getDataSource()
        const columns = pipeline.getColumns()
        const minSelectRowIndex = Math.min(selectStartRowIndex, selectEndRowIndex)
        const maxSelectRowIndex = Math.max(selectStartRowIndex, selectEndRowIndex)
        const minSelectColumnIndex = Math.min(selectStartColumnIndex, selectEndColumnIndex) + columnOffset
        const maxSelectColumnIndex = Math.max(selectStartColumnIndex, selectEndColumnIndex) + columnOffset
        const tableCopyValue = []
        for (let rowIndex = minSelectRowIndex; rowIndex <= maxSelectRowIndex; rowIndex++) {
            const record = dataSource[rowIndex]
            const rowCopyValue = []
            for (let columnIndex = minSelectColumnIndex; columnIndex <= maxSelectColumnIndex; columnIndex++) {
                const column = columns[columnIndex]
                const value = record[column.code]
                let copyValue = column.copyValue || value || ''
                if (typeof copyValue === 'function') {
                    copyValue = `"${copyValue(value, record, rowIndex)}"` || ''
                } else {
                    copyValue = copyValue.toString().replaceAll(/[\t\n\r]/g, '')
                }
                rowCopyValue.push(copyValue)
            }
            tableCopyValue.push(rowCopyValue.join('\t'))
        }
        return tableCopyValue.join('\n')
    }

    pipeline.clearSelection = () => {
        pipeline.setStateAtKey(stateKey, {
            ...state,
            mouseDown: false,
            isDragging: false,
            hasSelection: false,
            selectStartRowIndex: undefined,
            selectStartColumnIndex: undefined,
            selectEndRowIndex: undefined,
            selectEndColumnIndex: undefined,
        })
    }

    return pipeline
}

export default dragSelectPipeline
